// Hero for donor / carrier homes. Uses the canonical Hero molecule for
// the actionable framing (title + journey description + the main CTA),
// then renders the shared CompensationPlaceholderCard (an honest
// coming-soon card) instead of a fabricated dollar amount, since real
// compensation ranges depend on partner clinics / agencies. The MH CTA
// gating still uses `useCompletion` (same hook as MedicalHistoryStepCard).
import { Heart } from "lucide-react-native";
import { View } from "react-native";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { useCompletion } from "../screens/care/sections/MedicalHistoryCard";
import HelperQuickProfile from "./HelperQuickProfile";
import CompensationPlaceholderCard from "./CompensationPlaceholderCard";

type Role = "donor" | "carrier";

type RoleCopy = {
  eyebrow: string;
  title: string;
  goalLine: string;
};

const ROLE_COPY: Record<Role, RoleCopy> = {
  donor: {
    eyebrow: "Donor journey",
    title: "Let's help someone build their family",
    goalLine:
      "Tell us about you so we can match you to the right clinic or agency.",
  },
  carrier: {
    eyebrow: "Surrogate journey",
    title: "Let's help bring someone's family into the world",
    goalLine:
      "Tell us about you so we can match you to the right agency and intended parents.",
  },
};

type Props = {
  role: Role;
  onCompleteMedicalHistory?: () => void;
};

export default function HelperJourneyHero({
  role,
  onCompleteMedicalHistory,
}: Props) {
  const { progressPercent, isLoading } = useCompletion();
  const isUnlocked = !isLoading && progressPercent >= 100;
  const copy = ROLE_COPY[role];

  return (
    <View style={{ marginTop: 4, marginBottom: 16, gap: 12 }}>
      <Hero
        eyebrow={{ label: copy.eyebrow, icon: Heart }}
        title={copy.title}
        summary={copy.goalLine}
        primaryAction={
          !isUnlocked && onCompleteMedicalHistory
            ? {
                label:
                  progressPercent === 0
                    ? "Start medical history"
                    : "Continue medical history",
                onPress: onCompleteMedicalHistory,
              }
            : undefined
        }
      />

      <CompensationPlaceholderCard role={role} />

      <HelperQuickProfile role={role} />
    </View>
  );
}
